import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Settings,
  CreditCard,
  Bell,
  Shield,
  User,
  Building2,
  Zap,
  Crown,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Loader2,
  Calendar,
  XCircle,
  Mail,
  Key,
  UserPen,
  Users,
  UserPlus,
  Trash2,
  RefreshCw,
  MoreVertical,
  UserCog,
  Database,
  Clock,
  FileText,
  AlertTriangle,
  Download,
  Upload,
  FileSignature,
  PenLine,
  Plus,
  Star,
  Pencil,
  MapPin,
  Receipt,
  HelpCircle,
  ChevronRight,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Lock,
  Info,
  ShieldCheck,
  Wrench,
  MessageCircle,
} from "lucide-react";
import WhatsAppOpsBindingsPanel from "../components/WhatsAppOpsBindingsPanel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import PageHeader from "../components/PageHeader";
import PushNotificationsSettings from "../components/PushNotifications";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import { canManageManutenzioni } from "../utils/manutenzioniAccess";
import FatturazioneElettronicaImpostazioni from "../components/FatturazioneElettronicaImpostazioni";
import ImpostazioniRettaMensile from "../components/ImpostazioniRettaMensile";
import MediciCurantiSection from "../components/doctors/MediciCurantiSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_STRUTTURA_OPTIONS = [
  "RSA", "Casa di Riposo", "Comunità Alloggio", "Residenza Protetta",
  "Centro Diurno", "Hospice", "Comunità Integrata", "Altro"
];

// ==================== COMPONENTI HELPER PERMESSI ====================

const ROLE_ICONS = {
  super_admin: Crown,
  amministrativo: Building2,
  coordinatore: UserCog,
  operatore: User,
};

const ROLE_COLORS = {
  super_admin: "bg-purple-100 text-purple-800 border-purple-200",
  amministrativo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  coordinatore: "bg-blue-100 text-blue-800 border-blue-200",
  operatore: "bg-slate-100 text-slate-700 border-slate-200",
};

function samePermissionSet(a, b) {
  const A = new Set(a || []);
  const B = new Set(b || []);
  if (A.size !== B.size) return false;
  return [...A].every((p) => B.has(p));
}

function normalizePermissionList(list) {
  if (!Array.isArray(list)) return [];
  return [...new Set(list.filter((p) => typeof p === "string" && p.length > 0))].sort();
}

function PermissionsChecklist({ catalog, selectedPermissions, onChange, onToggle, readOnly = false }) {
  if (!catalog) return null;
  const groups = {};
  catalog.permissions.forEach(p => {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  });
  const groupNames = Object.keys(groups);

  return (
    <div className="space-y-3">
      {groupNames.map(group => (
        <div key={group}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{group}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {groups[group].map(perm => {
              const checked = selectedPermissions.includes(perm.id);
              return (
                <label
                  key={perm.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer select-none transition-colors ${
                    readOnly ? "cursor-default" : "hover:bg-slate-50"
                  } ${checked ? "text-slate-900" : "text-slate-400"}`}
                >
                  {readOnly ? (
                    checked
                      ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      : <X className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                  ) : (
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(val) => {
                        if (onToggle) {
                          onToggle(perm.id, val === true);
                          return;
                        }
                        if (val === true) {
                          if (!selectedPermissions.includes(perm.id)) {
                            onChange([...selectedPermissions, perm.id]);
                          }
                        } else if (val === false) {
                          onChange(selectedPermissions.filter(p => p !== perm.id));
                        }
                      }}
                      className="flex-shrink-0"
                    />
                  )}
                  <span className={checked ? "" : "line-through opacity-50"}>{perm.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Impostazioni() {
  const { user, token, logout, updateUser, refreshUser, hasPermission, strutture, planTier, maxStrutture } = useAuth();

  // Tab attiva della pagina impostazioni (?tab=whatsapp-ops dopo checkout Stripe)
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get("tab");
      return tab || "struttura";
    } catch {
      return "struttura";
    }
  });

  // Abbonamento recruiting
  const [recruitingPlan, setRecruitingPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Modal modifica credenziali
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingCredentials, setSavingCredentials] = useState(false);
  
  // Gestione Utenti (super_admin e amministrativo, vedi canManageOrgUsers)
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showEditPermissionsModal, setShowEditPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", nome: "", password: "", role: "operatore", permissions: null });
  const [resetPassword, setResetPassword] = useState("");
  const [savingUser, setSavingUser] = useState(false);
  // Permessi custom per creazione/modifica utente (null = usa default del ruolo)
  const [customPermissions, setCustomPermissions] = useState(null);
  const [editCustomPermissions, setEditCustomPermissions] = useState([]);
  // Catalogo permessi dal server
  const [permissionsCatalog, setPermissionsCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  
  // Notifiche (mock per ora)
  const [notifications, setNotifications] = useState({
    email_richieste: true,
    email_cv: true,
    email_promo: false,
    email_pai: true
  });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loadingMarketingConsent, setLoadingMarketingConsent] = useState(false);

  // Data Retention (solo super_admin e amministrativo)
  const [retentionConfig, setRetentionConfig] = useState(null);
  const [retentionReport, setRetentionReport] = useState(null);
  const [loadingRetention, setLoadingRetention] = useState(false);
  const [savingRetention, setSavingRetention] = useState(false);
  const [showRetentionReport, setShowRetentionReport] = useState(false);

  // Contratto Template
  const [contractTemplate, setContractTemplate] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  const [contractBodyText, setContractBodyText] = useState("");
  const [contractLogoFile, setContractLogoFile] = useState(null);
  const [contractLogoPreview, setContractLogoPreview] = useState(null);  // preview file locale
  const [contractLogoSavedUrl, setContractLogoSavedUrl] = useState(null); // blob URL del logo salvato
  const [showContractTextPreview, setShowContractTextPreview] = useState(false);
  const [extractingPdfText, setExtractingPdfText] = useState(false);

  // Gestione Strutture
  const [orgStrutture, setOrgStrutture] = useState([]);
  const [loadingStrutture, setLoadingStrutture] = useState(false);
  const [showAddStrutturaModal, setShowAddStrutturaModal] = useState(false);
  const [editingStruttura, setEditingStruttura] = useState(null);
  const [savingStruttura, setSavingStruttura] = useState(false);
  const [loadingZohoStruttura, setLoadingZohoStruttura] = useState(false);
  const [zohoStrutturaLoaded, setZohoStrutturaLoaded] = useState(false);
  const [strutturaForm, setStrutturaForm] = useState({
    name: "", tipoStruttura: "", postiTotali: "", telefonoStruttura: "",
    comune: "", via: "", numeroCivico: "", provincia: "",
    tariffaDoppiaWc: "", tariffaSingolaWc: "", zohoRecordId: "",
  });
  const [orgMaxStrutture, setOrgMaxStrutture] = useState(maxStrutture || 1);
  const [orgPlanTier, setOrgPlanTier] = useState(planTier || "tier_1");

  // Retention: modifica solo super_admin (e ruoli legacy)
  const isSuperAdminRole =
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    user?.role === "Responsabile";
  // Gestione utenti: come permesso backend (super_admin + amministrativo)
  const canManageOrgUsers = hasPermission("users:create");
  const canManageStrutture = hasPermission('struttura:update');
  const canToggleManutenzioni = canManageManutenzioni(user);
  const canViewRetention = user?.role === 'super_admin' || user?.role === 'amministrativo';
  const usersExcludingSelf = users.filter((u) => String(u.id) !== String(user?.id));
  const [togglingManutenzioni, setTogglingManutenzioni] = useState(false);

  useEffect(() => {
    fetchRecruitingPlan();
    if (canManageOrgUsers) {
      fetchUsers();
    }
    if (canManageStrutture || canAccessFatturazioneElettronica(user)) {
      fetchStrutture();
    }
    if (canViewRetention) {
      fetchRetentionConfig();
    }
    fetchContractTemplate();
    fetchMarketingConsent();
    if (isSuperAdminRole) {
      fetchPermissionsCatalog();
    }
  }, [token, canManageOrgUsers, canManageStrutture, canViewRetention, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMarketingConsent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/marketing/consenso`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMarketingConsent(!!response.data?.granted);
    } catch (error) {
      console.error("Errore caricamento consenso marketing:", error);
    }
  };

  const updateMarketingConsent = async (granted) => {
    setLoadingMarketingConsent(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/marketing/consenso`,
        { granted },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMarketingConsent(!!response.data?.granted);
      toast.success(granted ? "Consenso marketing attivato" : "Consenso marketing revocato");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile aggiornare il consenso");
    } finally {
      setLoadingMarketingConsent(false);
    }
  };

  // ==================== GESTIONE STRUTTURE ====================

  const fetchStrutture = async () => {
    setLoadingStrutture(true);
    try {
      const response = await axios.get(`${API_URL}/api/strutture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgStrutture(response.data.strutture || []);
      setOrgMaxStrutture(response.data.max_strutture || 1);
      setOrgPlanTier(response.data.plan_tier || "tier_1");
    } catch (error) {
      console.error("Errore caricamento strutture:", error);
    } finally {
      setLoadingStrutture(false);
    }
  };

  const resetStrutturaForm = () => {
    setStrutturaForm({
      name: "", tipoStruttura: "", postiTotali: "", telefonoStruttura: "",
      comune: "", via: "", numeroCivico: "", provincia: "",
      tariffaDoppiaWc: "", tariffaSingolaWc: "", zohoRecordId: "",
    });
    setEditingStruttura(null);
  };

  const fetchZohoStruttura = async (zohoId) => {
    if (!zohoId || zohoId.length < 10) return;
    setLoadingZohoStruttura(true);
    try {
      const response = await axios.get(`${API_URL}/api/billing/zoho-struttura/${zohoId}`);
      if (response.data.success && response.data.data) {
        const z = response.data.data;
        setStrutturaForm(prev => ({
          ...prev,
          name: z.organizationName || prev.name,
          tipoStruttura: z.tipoStruttura || prev.tipoStruttura,
          postiTotali: z.postiTotali || prev.postiTotali,
          telefonoStruttura: z.telefonoStruttura || prev.telefonoStruttura,
          comune: z.comune || prev.comune,
          via: z.via || prev.via,
          numeroCivico: z.numeroCivico || prev.numeroCivico,
          provincia: z.provincia || prev.provincia,
          tariffaDoppiaWc: z.tariffaDoppiaWc || prev.tariffaDoppiaWc,
          tariffaSingolaWc: z.tariffaSingolaWc || prev.tariffaSingolaWc,
        }));
        setZohoStrutturaLoaded(true);
        toast.success("Dati struttura caricati da Zoho! Verifica e completa i campi mancanti.");
      }
    } catch (error) {
      console.error("Error fetching Zoho struttura:", error);
      const message = error.response?.data?.detail || "Impossibile recuperare i dati da Zoho. Verifica l'ID inserito.";
      toast.error(message);
      setZohoStrutturaLoaded(false);
    } finally {
      setLoadingZohoStruttura(false);
    }
  };

  const handleZohoIdChangeStruttura = (value) => {
    setStrutturaForm(prev => ({ ...prev, zohoRecordId: value }));
    setZohoStrutturaLoaded(false);
    if (window._zohoStrutturaTimeout) {
      clearTimeout(window._zohoStrutturaTimeout);
    }
    if (value && value.length >= 15) {
      window._zohoStrutturaTimeout = setTimeout(() => {
        fetchZohoStruttura(value);
      }, 800);
    }
  };

  const handleOpenAddStruttura = () => {
    resetStrutturaForm();
    setZohoStrutturaLoaded(false);
    setShowAddStrutturaModal(true);
  };

  const handleOpenEditStruttura = (struttura) => {
    setEditingStruttura(struttura);
    setStrutturaForm({
      name: struttura.name || "",
      tipoStruttura: struttura.tipoStruttura || "",
      postiTotali: struttura.postiTotali?.toString() || "",
      telefonoStruttura: struttura.telefonoStruttura || "",
      comune: struttura.comune || "",
      via: struttura.via || "",
      numeroCivico: struttura.numeroCivico || "",
      provincia: struttura.provincia || "",
      tariffaDoppiaWc: struttura.tariffaDoppiaWc?.toString() || "",
      tariffaSingolaWc: struttura.tariffaSingolaWc?.toString() || "",
      zohoRecordId: struttura.zohoRecordId || "",
    });
    setShowAddStrutturaModal(true);
  };

  const handleSaveStruttura = async () => {
    if (!strutturaForm.name || !strutturaForm.tipoStruttura || !strutturaForm.postiTotali ||
        !strutturaForm.telefonoStruttura || !strutturaForm.comune || !strutturaForm.via ||
        !strutturaForm.numeroCivico || !strutturaForm.provincia) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    const tariffaDoppia = parseFloat(strutturaForm.tariffaDoppiaWc);
    const tariffaSingola = parseFloat(strutturaForm.tariffaSingolaWc);
    if (strutturaForm.tariffaDoppiaWc === "" || isNaN(tariffaDoppia) || tariffaDoppia < 0 ||
        strutturaForm.tariffaSingolaWc === "" || isNaN(tariffaSingola) || tariffaSingola < 0) {
      toast.error("Inserisci le tariffe (0 se la struttura non ha quel tipo di camera)");
      return;
    }

    setSavingStruttura(true);
    try {
      const payload = {
        ...strutturaForm,
        postiTotali: parseInt(strutturaForm.postiTotali),
        tariffaDoppiaWc: tariffaDoppia,
        tariffaSingolaWc: tariffaSingola,
        zohoRecordId: strutturaForm.zohoRecordId || null,
      };

      if (editingStruttura) {
        await axios.put(`${API_URL}/api/strutture/${editingStruttura.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Struttura aggiornata con successo");
      } else {
        await axios.post(`${API_URL}/api/strutture`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Struttura aggiunta con successo");
      }
      setShowAddStrutturaModal(false);
      resetStrutturaForm();
      fetchStrutture();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingStruttura(false);
    }
  };

  const handleDeleteStruttura = async (id) => {
    if (!window.confirm("Eliminare questa struttura?")) return;
    try {
      await axios.delete(`${API_URL}/api/strutture/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Struttura eliminata");
      fetchStrutture();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // ==================== CONTRACT TEMPLATE ====================

  const fetchContractLogo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contract-template/logo?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      setContractLogoSavedUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch {
      setContractLogoSavedUrl(null);
    }
  };

  const fetchContractTemplate = async () => {
    setLoadingContract(true);
    try {
      const response = await axios.get(`${API_URL}/api/contract-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tmpl = response.data.template;
      setContractTemplate(tmpl);
      if (tmpl) {
        setContractBodyText(tmpl.body_text || "");
        if (tmpl.has_logo) fetchContractLogo();
      }
    } catch (error) {
      console.error("Errore caricamento contratto:", error);
    } finally {
      setLoadingContract(false);
    }
  };

  const handleContractLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Il logo deve essere PNG, JPG o WEBP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Il logo non può superare 2 MB");
      return;
    }
    setContractLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setContractLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveContractLogo = () => {
    setContractLogoFile(null);
    setContractLogoPreview(null);
    setContractLogoSavedUrl(null);
  };

  const handleSaveContractTemplate = async () => {
    if (!contractBodyText.trim()) {
      toast.error("Il testo del contratto è obbligatorio");
      return;
    }
    setSavingContract(true);
    const formData = new FormData();
    formData.append("body_text", contractBodyText);
    if (contractLogoFile) {
      formData.append("logo", contractLogoFile);
    }
    try {
      const response = await axios.post(`${API_URL}/api/contract-template`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setContractTemplate(response.data.template);
      setContractLogoFile(null);
      setContractLogoPreview(null);
      setContractLogoSavedUrl(null);
      if (response.data.template?.has_logo) fetchContractLogo();
      toast.success("Template contratto salvato!");
    } catch (error) {
      console.error("Errore salvataggio contratto:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingContract(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!window.confirm("Eliminare il template del contratto?")) return;
    try {
      await axios.delete(`${API_URL}/api/contract-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractTemplate(null);
      setContractBodyText("");
      setContractLogoFile(null);
      setContractLogoPreview(null);
      setContractLogoSavedUrl(null);
      toast.success("Contratto eliminato");
    } catch (error) {
      console.error("Errore eliminazione contratto:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleExtractPdfText = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Il file deve essere in formato PDF");
      return;
    }
    setExtractingPdfText(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        `${API_URL}/api/contract-template/extract-text`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const extracted = response.data.text || "";
      if (!extracted.trim()) {
        toast.warning("Il PDF non contiene testo selezionabile (potrebbe essere una scansione).");
      } else {
        setContractBodyText(extracted);
        toast.success(`Testo estratto da ${response.data.pages} pagina${response.data.pages !== 1 ? "e" : ""}`);
      }
    } catch (error) {
      console.error("Errore estrazione testo PDF:", error);
      toast.error(error.response?.data?.detail || "Impossibile estrarre il testo dal PDF");
    } finally {
      setExtractingPdfText(false);
      e.target.value = "";
    }
  };

  const fetchRecruitingPlan = async () => {
    setLoadingPlan(true);
    try {
      const response = await axios.get(`${API_URL}/api/recruiting/my-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruitingPlan(response.data);
    } catch (error) {
      console.error("Errore caricamento piano:", error);
    } finally {
      setLoadingPlan(false);
    }
  };

  // ==================== DATA RETENTION ====================

  const fetchRetentionConfig = async () => {
    setLoadingRetention(true);
    try {
      const response = await axios.get(`${API_URL}/api/retention/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRetentionConfig(response.data);
    } catch (error) {
      console.error("Errore caricamento config retention:", error);
    } finally {
      setLoadingRetention(false);
    }
  };

  const fetchRetentionReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/retention/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRetentionReport(response.data);
      setShowRetentionReport(true);
    } catch (error) {
      console.error("Errore caricamento report retention:", error);
      toast.error("Errore nel caricamento del report");
    }
  };

  const saveRetentionConfig = async () => {
    if (!isSuperAdminRole) {
      toast.error("Solo super_admin può modificare la configurazione");
      return;
    }
    
    setSavingRetention(true);
    try {
      await axios.put(`${API_URL}/api/retention/config`, {
        retention_days: retentionConfig.retention_days,
        auto_delete_enabled: retentionConfig.auto_delete_enabled,
        notification_days_before: retentionConfig.notification_days_before
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione retention salvata");
    } catch (error) {
      console.error("Errore salvataggio config:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingRetention(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'expired':
        return <Badge variant="destructive">Scaduto</Badge>;
      case 'expiring_soon':
        return <Badge variant="warning" className="bg-amber-100 text-amber-800">In Scadenza</Badge>;
      default:
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Attivo</Badge>;
    }
  };

  // ==================== GESTIONE UTENTI ====================
  
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Errore caricamento utenti:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.nome || !newUser.password) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    setSavingUser(true);
    try {
      const defaultPerms = normalizePermissionList(getDefaultPermissionsForRole(newUser.role));
      const chosen =
        customPermissions === null
          ? null
          : normalizePermissionList(customPermissions);
      const differsFromRoleDefault =
        chosen !== null && !samePermissionSet(chosen, defaultPerms);
      const payload = {
        email: newUser.email,
        nome: newUser.nome,
        password: newUser.password,
        role: newUser.role,
        ...(differsFromRoleDefault ? { permissions: chosen } : {}),
      };
      await axios.post(`${API_URL}/api/users`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Utente ${newUser.nome} creato con successo!`);
      setShowCreateUserModal(false);
      setNewUser({ email: "", nome: "", password: "", role: "operatore", permissions: null });
      setCustomPermissions(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione utente");
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ruolo aggiornato!");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      if (isActive) {
        await axios.delete(`${API_URL}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Utente disattivato");
      } else {
        await axios.put(`${API_URL}/api/users/${userId}/reactivate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Utente riattivato");
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'operazione");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    setSavingUser(true);
    try {
      await axios.post(`${API_URL}/api/users/${selectedUser.id}/reset-password`, 
        { new_password: resetPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password resettata con successo!");
      setShowResetPasswordModal(false);
      setResetPassword("");
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset password");
    } finally {
      setSavingUser(false);
    }
  };

  const fetchPermissionsCatalog = async () => {
    setLoadingCatalog(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/permissions/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissionsCatalog(response.data);
    } catch (error) {
      console.error("Errore caricamento catalogo permessi:", error);
    } finally {
      setLoadingCatalog(false);
    }
  };

  const getDefaultPermissionsForRole = useCallback((role) => {
    if (!permissionsCatalog) return [];
    const roleData = permissionsCatalog.roles.find(r => r.id === role);
    return roleData?.default_permissions || [];
  }, [permissionsCatalog]);

  const handleOpenEditPermissions = (u) => {
    setSelectedUser(u);
    const rawPerms = u.permissions;
    const existing = Array.isArray(rawPerms) && rawPerms.length > 0
      ? normalizePermissionList(rawPerms)
      : normalizePermissionList(getDefaultPermissionsForRole(u.role));
    setEditCustomPermissions(existing);
    setShowEditPermissionsModal(true);
  };

  const handleSaveUserPermissions = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    try {
      const defaultPerms = normalizePermissionList(getDefaultPermissionsForRole(selectedUser.role));
      const chosen = normalizePermissionList(editCustomPermissions);
      const isDefault = samePermissionSet(chosen, defaultPerms);
      const payload = { permissions: isDefault ? [] : chosen };
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Permessi aggiornati con successo!");
      setShowEditPermissionsModal(false);
      setSelectedUser(null);
      setEditCustomPermissions([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento permessi");
    } finally {
      setSavingUser(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-purple-500 text-white"><Crown className="w-3 h-3 mr-1" />Super Admin</Badge>;
      case 'admin':
        return <Badge className="bg-violet-600 text-white"><Building2 className="w-3 h-3 mr-1" />Amministratore</Badge>;
      case 'amministrativo':
        return <Badge className="bg-emerald-500 text-white"><Building2 className="w-3 h-3 mr-1" />Amministrativo</Badge>;
      case 'coordinatore':
        return <Badge className="bg-blue-500 text-white"><UserCog className="w-3 h-3 mr-1" />Coordinatore</Badge>;
      default:
        return <Badge variant="outline"><User className="w-3 h-3 mr-1" />Operatore</Badge>;
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error("Inserisci un'email valida");
      return;
    }
    
    setSavingCredentials(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-email`, 
        { new_email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email aggiornata! Effettua nuovamente il login.");
      setShowEmailModal(false);
      setNewEmail("");
      // Logout per far riloggare con la nuova email
      setTimeout(() => logout(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento email");
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Compila tutti i campi");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    if (newPassword.length < 10) {
      toast.error("La nuova password deve avere almeno 10 caratteri (vedi requisiti sotto)");
      return;
    }
    
    setSavingCredentials(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password aggiornata con successo!");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento password");
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleChangeName = async () => {
    if (!newName || newName.trim().length < 2) {
      toast.error("Il nome deve avere almeno 2 caratteri");
      return;
    }
    
    setSavingCredentials(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/change-name`, 
        { new_name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Nome aggiornato con successo!");
      setShowNameModal(false);
      setNewName("");
      // Aggiorna il nome nell'AuthContext immediatamente
      updateUser({ name: response.data.new_name });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento nome");
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/recruiting/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abbonamento disdetto. Rimarrà attivo fino alla scadenza del periodo corrente.");
      fetchRecruitingPlan();
      setShowCancelModal(false);
    } catch (error) {
      console.error("Errore disdetta:", error);
      toast.error("Errore nella disdetta. Contatta l'assistenza.");
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgradePlan = () => {
    window.location.href = "/personale";
  };

  // Definizione dei tab della pagina impostazioni (visibilità condizionata dai permessi)
  const tabs = [
    { id: "struttura", label: "Struttura", icon: Building2, show: true },
    { id: "fiscali", label: "Dati fiscali", icon: Receipt, show: canAccessFatturazioneElettronica(user) },
    { id: "pagamenti", label: "Pagamenti", icon: CreditCard, show: true },
    { id: "abbonamento", label: "Abbonamento", icon: Crown, show: true },
    { id: "utenti", label: "Utenti", icon: Users, show: true },
    {
      id: "whatsapp-ops",
      label: "WhatsApp operativo",
      icon: MessageCircle,
      show: canManageOrgUsers && user?.whatsapp_ops_enabled === true,
    },
    { id: "supporto", label: "Supporto", icon: HelpCircle, show: true },
  ].filter(t => t.show);

  // Se l'utente non ha accesso al tab corrente, fallback al primo disponibile
  const safeActiveTab = tabs.some(t => t.id === activeTab) ? activeTab : (tabs[0]?.id || "struttura");

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-screen-2xl mx-auto w-full">
      {/* Page Header - responsive */}
      <PageHeader
        title="Impostazioni"
        subtitle="Gestisci il tuo account e le preferenze"
        icon={Settings}
        iconContainerClassName="bg-slate-100"
        iconClassName="text-slate-600"
      />

      <div className="flex flex-col lg:flex-row gap-4 md:gap-6 xl:gap-8">
        {/* Sidebar navigazione */}
        <aside className="lg:w-52 xl:w-56 lg:flex-shrink-0">
          <Card className="card-base lg:sticky lg:top-4">
            <CardContent className="p-2">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible" aria-label="Sezioni impostazioni">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = safeActiveTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 md:gap-3 px-3 py-2 md:py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap lg:w-full ${
                        isActive
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-slate-700" : "text-slate-400"}`} />
                      <span className="flex-1 text-left">{tab.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4 hidden lg:block text-slate-400" />}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </aside>

        {/* Area contenuti */}
        <div className="flex-1 min-w-0 space-y-4 md:space-y-6">
        {/* ========== TAB: ABBONAMENTO ========== */}
        {safeActiveTab === "abbonamento" && (<>
        {/* SEZIONE: ABBONAMENTI */}
        <Card className="card-base">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-violet-500" />
              Abbonamenti
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Gestisci i tuoi piani attivi
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0 space-y-4">
            {/* Piano Recruiting */}
            <div className="border border-slate-200 rounded-xl p-3 md:p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center ${
                    recruitingPlan?.plan === 'pro' 
                      ? 'bg-amber-100' 
                      : recruitingPlan?.plan === 'base' 
                        ? 'bg-blue-100' 
                        : 'bg-slate-100'
                  }`}>
                    {recruitingPlan?.plan === 'pro' ? (
                      <Crown className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
                    ) : recruitingPlan?.plan === 'base' ? (
                      <Zap className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                    ) : (
                      <User className="w-4 h-4 md:w-5 md:h-5 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm md:text-base font-semibold text-slate-900">Recruiting Personale</p>
                    <p className="text-xs md:text-sm text-slate-500">
                      {recruitingPlan?.plan 
                        ? `Piano ${recruitingPlan.plan.toUpperCase()}` 
                        : 'Nessun piano attivo'}
                    </p>
                  </div>
                </div>
                
                {loadingPlan ? (
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin text-slate-400" />
                ) : recruitingPlan?.active ? (
                  <Badge className={`text-xs md:text-sm ${
                    recruitingPlan.plan === 'pro' 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-blue-500 text-white'
                  }`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Attivo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs md:text-sm text-slate-500">
                    Non attivo
                  </Badge>
                )}
              </div>
              
              {recruitingPlan?.active && (
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Prezzo</span>
                    <span className="font-semibold">
                      {recruitingPlan.plan === 'pro' ? '89€' : '49€'}/mese
                    </span>
                  </div>
                  {recruitingPlan.activated_at && (
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-slate-600">Attivato il</span>
                      <span className="text-slate-900">
                        {new Date(recruitingPlan.activated_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-2 flex-wrap">
                {recruitingPlan?.active ? (
                  <>
                    {recruitingPlan.plan === 'base' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 min-w-[120px] border-amber-300 text-amber-600 hover:bg-amber-50 text-xs md:text-sm"
                        onClick={handleUpgradePlan}
                      >
                        <Crown className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                        Passa a PRO
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50 hover:border-red-300 text-xs md:text-sm"
                      onClick={() => setShowCancelModal(true)}
                    >
                      Disdici
                    </Button>
                  </>
                ) : (
                  <Button 
                    size="sm"
                    className="flex-1 bg-violet-600 hover:bg-violet-700 text-xs md:text-sm"
                    onClick={handleUpgradePlan}
                  >
                    <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    Attiva piano
                  </Button>
                )}
              </div>
            </div>
            
            {/* Piano Base Affidya (sempre attivo) */}
            <div className="border border-emerald-200 rounded-xl p-3 md:p-4 bg-emerald-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Building2 className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm md:text-base font-semibold text-slate-900">Affidya Base</p>
                    <p className="text-xs md:text-sm text-slate-500">Gestionale struttura</p>
                  </div>
                </div>
                <Badge className="bg-emerald-500 text-white text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Incluso
                </Badge>
              </div>
              <p className="text-xs text-emerald-700 mt-3">
                Dashboard, Fascicoli Assistenziali, Camere e posti letto, Profili CV gratuiti e tutte le funzionalità base.
              </p>
            </div>
          </CardContent>
        </Card>
        </>)}
        {/* ========== FINE TAB: ABBONAMENTO ========== */}

        {/* ========== TAB: STRUTTURA ========== */}
        {safeActiveTab === "struttura" && (<>
        {/* SEZIONE: GESTIONE STRUTTURE */}
        {canManageStrutture && (
          <Card className="card-base border-teal-200">
            <CardHeader className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Building2 className="w-4 h-4 md:w-5 md:h-5 text-teal-500" />
                    Gestione Strutture
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    {orgStrutture.length} di {orgMaxStrutture === 999 ? "illimitate" : orgMaxStrutture} strutture
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={handleOpenAddStruttura}
                  disabled={orgStrutture.length >= orgMaxStrutture}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
              {loadingStrutture ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
                </div>
              ) : orgStrutture.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Nessuna struttura configurata.</p>
              ) : (
                <div className="space-y-3">
                  {orgStrutture.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-teal-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-slate-900 truncate">{s.name}</span>
                            {s.is_primary && (
                              <Star className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{s.tipoStruttura}</span>
                            {s.comune && (
                              <>
                                <span className="text-slate-300">|</span>
                                <span className="flex items-center gap-0.5">
                                  <MapPin className="w-3 h-3" />
                                  {s.comune} ({s.provincia})
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenEditStruttura(s)}
                        >
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        {!s.is_primary && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteStruttura(s.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {orgStrutture.length >= 2 && (
                    <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-200">
                      Per passare da una struttura all&apos;altra usa il menu a sinistra (desktop) o il menu in alto (mobile): clicca sul nome della struttura attiva.
                    </p>
                  )}
                </div>
              )}
              {orgStrutture.length >= orgMaxStrutture && orgMaxStrutture < 999 && (
                <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Limite raggiunto. Effettua un upgrade del piano per aggiungere altre strutture.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {canToggleManutenzioni ? (
          <Card className="card-base border-amber-200">
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                <Wrench className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
                Manutenzioni
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                Attiva il modulo per tracciare richieste di intervento e spese di manutenzione sulla struttura attiva
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
              <div className="flex items-center justify-between gap-3 py-1">
                <div className="min-w-0">
                  <Label className="text-sm md:text-base font-medium">Modulo Manutenzioni</Label>
                  <p className="text-xs md:text-sm text-slate-500">
                    Se attivo, compare nel menu e gli OSS con tag Responsabile possono aprire richieste dal portale OSS.
                  </p>
                </div>
                <Switch
                  checked={!!user?.manutenzioni_enabled}
                  disabled={togglingManutenzioni}
                  onCheckedChange={async (checked) => {
                    setTogglingManutenzioni(true);
                    try {
                      await axios.put(
                        `${API_URL}/api/manutenzioni/config`,
                        { enabled: checked },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      await refreshUser();
                      toast.success(checked ? "Modulo Manutenzioni attivato" : "Modulo Manutenzioni disattivato");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Errore aggiornamento modulo");
                    } finally {
                      setTogglingManutenzioni(false);
                    }
                  }}
                  className="shrink-0"
                />
              </div>
            </CardContent>
          </Card>
        ) : null}

        <MediciCurantiSection token={token} canManage={canManageStrutture} />

        {/* Modal Aggiungi/Modifica Struttura */}
        <Dialog open={showAddStrutturaModal} onOpenChange={setShowAddStrutturaModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStruttura ? "Modifica Struttura" : "Aggiungi Struttura"}</DialogTitle>
              <DialogDescription>
                {editingStruttura ? "Modifica i dati della struttura" : "Inserisci i dati della nuova struttura"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {!editingStruttura && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                  <Label className="text-sm font-medium">ID struttura (legacy, opzionale)</Label>
                  <div className="relative">
                    <Input
                      value={strutturaForm.zohoRecordId}
                      onChange={(e) => handleZohoIdChangeStruttura(e.target.value)}
                      placeholder="es. 876565000000566102"
                      className={zohoStrutturaLoaded ? "border-green-400 pr-8" : ""}
                    />
                    {loadingZohoStruttura && (
                      <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-teal-500" />
                    )}
                    {zohoStrutturaLoaded && !loadingZohoStruttura && (
                      <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    Se la struttura esiste già, inserisci il suo ID (legacy) per caricare automaticamente i dati e collegarla.
                  </p>
                </div>
              )}
              <div>
                <Label>Nome Struttura *</Label>
                <Input
                  value={strutturaForm.name}
                  onChange={(e) => setStrutturaForm({...strutturaForm, name: e.target.value})}
                  placeholder="es. Residenza Il Giardino"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo *</Label>
                  <Select
                    value={strutturaForm.tipoStruttura}
                    onValueChange={(val) => setStrutturaForm({...strutturaForm, tipoStruttura: val})}
                  >
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {TIPO_STRUTTURA_OPTIONS.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Posti Totali *</Label>
                  <Input
                    type="number"
                    value={strutturaForm.postiTotali}
                    onChange={(e) => setStrutturaForm({...strutturaForm, postiTotali: e.target.value})}
                    placeholder="es. 80"
                  />
                </div>
              </div>
              <div>
                <Label>Telefono Struttura *</Label>
                <Input
                  value={strutturaForm.telefonoStruttura}
                  onChange={(e) => setStrutturaForm({...strutturaForm, telefonoStruttura: e.target.value})}
                  placeholder="es. 0471 123456"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Comune *</Label>
                  <Input
                    value={strutturaForm.comune}
                    onChange={(e) => setStrutturaForm({...strutturaForm, comune: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Provincia *</Label>
                  <Input
                    value={strutturaForm.provincia}
                    onChange={(e) => setStrutturaForm({...strutturaForm, provincia: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Via *</Label>
                  <Input
                    value={strutturaForm.via}
                    onChange={(e) => setStrutturaForm({...strutturaForm, via: e.target.value})}
                  />
                </div>
                <div>
                  <Label>N. Civico *</Label>
                  <Input
                    value={strutturaForm.numeroCivico}
                    onChange={(e) => setStrutturaForm({...strutturaForm, numeroCivico: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tariffa Doppia WC *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={strutturaForm.tariffaDoppiaWc}
                    onChange={(e) => setStrutturaForm({...strutturaForm, tariffaDoppiaWc: e.target.value})}
                    placeholder="0 se non avete camere doppie"
                  />
                </div>
                <div>
                  <Label>Tariffa Singola WC *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={strutturaForm.tariffaSingolaWc}
                    onChange={(e) => setStrutturaForm({...strutturaForm, tariffaSingolaWc: e.target.value})}
                    placeholder="0 se non avete camere singole"
                  />
                </div>
              </div>
              {editingStruttura && (
                <div>
                  <Label>ID struttura (legacy, opzionale)</Label>
                  <Input
                    value={strutturaForm.zohoRecordId}
                    onChange={(e) => setStrutturaForm({...strutturaForm, zohoRecordId: e.target.value})}
                    placeholder="es. 876565000000566102"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    ID legacy collegato a questa struttura.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddStrutturaModal(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleSaveStruttura}
                disabled={savingStruttura}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {savingStruttura && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingStruttura ? "Salva modifiche" : "Aggiungi Struttura"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Card className="card-base border-indigo-200">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Mail className="h-4 w-4 text-indigo-600" />
              Aggiornamenti e opportunità
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Ricevi novità Affidya, bandi e contenuti utili per la gestione della struttura.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-4 px-4 pb-4 pt-0 md:px-6 md:pb-6">
            <p className="text-sm text-slate-600">Autorizzo l'invio di comunicazioni marketing all'email amministrativa della struttura.</p>
            <Switch
              checked={marketingConsent}
              disabled={loadingMarketingConsent}
              onCheckedChange={updateMarketingConsent}
              aria-label="Consenso alle comunicazioni marketing"
            />
          </CardContent>
        </Card>
        </>)}
        {/* Nota: la sezione STRUTTURA continua più sotto con Retention e Template Contratto */}

        {/* ========== TAB: UTENTI (parte 1/3 - Gestione team) ========== */}
        {safeActiveTab === "utenti" && (<>

        {/* SEZIONE: TIPI DI ADMIN E PERMESSI (solo super_admin) */}
        {isSuperAdminRole && (
          <Card className="card-base border-indigo-200 mb-4">
            <CardHeader className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-indigo-500" />
                    Tipi di Admin e Permessi
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    Riepilogo dei ruoli disponibili e dei permessi assegnati a ciascuno
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
              {loadingCatalog ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                </div>
              ) : permissionsCatalog ? (
                <Accordion type="multiple" className="space-y-2">
                  {permissionsCatalog.roles.map(roleInfo => {
                    const RoleIcon = ROLE_ICONS[roleInfo.id] || User;
                    const colorClass = ROLE_COLORS[roleInfo.id] || ROLE_COLORS.operatore;
                    return (
                      <AccordionItem
                        key={roleInfo.id}
                        value={roleInfo.id}
                        className={`rounded-lg border px-4 py-1 ${colorClass}`}
                      >
                        <AccordionTrigger className="hover:no-underline py-3">
                          <div className="flex items-center gap-3 text-left">
                            <RoleIcon className="w-4 h-4 flex-shrink-0" />
                            <div>
                              <p className="font-semibold text-sm">{roleInfo.label}</p>
                              <p className="text-xs font-normal opacity-70 mt-0.5">{roleInfo.description}</p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-3">
                          <div className="bg-white/60 rounded-lg p-3">
                            <PermissionsChecklist
                              catalog={permissionsCatalog}
                              selectedPermissions={roleInfo.default_permissions}
                              readOnly
                            />
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : (
                <div className="text-center py-6 text-slate-400 text-sm">
                  <Info className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Impossibile caricare il catalogo permessi
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SEZIONE: GESTIONE UTENTI (super_admin e amministrativo) */}
        {canManageOrgUsers && (
          <Card className="card-base border-purple-200" data-testid="users-management-section">
            <CardHeader className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Users className="w-4 h-4 md:w-5 md:h-5 text-purple-500" />
                    Gestione Utenti
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    Crea e gestisci gli accessi alla piattaforma
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => setShowCreateUserModal(true)}
                  data-testid="btn-create-user"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuovo Utente
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                </div>
              ) : usersExcludingSelf.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  {users.length === 0 ? (
                    <>
                      <p>Nessun utente trovato</p>
                      <p className="text-xs mt-1">Clicca &quot;Nuovo Utente&quot; per aggiungere operatori</p>
                    </>
                  ) : (
                    <>
                      <p>Nessun altro utente da mostrare</p>
                      <p className="text-xs mt-1">Il tuo account non compare in questo elenco. Gli altri utenti della struttura sono elencati qui.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {usersExcludingSelf.map((u) => (
                    <div 
                      key={u.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        u.is_active === false ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'
                      }`}
                      data-testid={`user-row-${u.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          u.role === 'super_admin' ? 'bg-purple-100' :
                          u.role === 'admin' ? 'bg-violet-100' :
                          u.role === 'coordinatore' ? 'bg-blue-100' : 'bg-slate-100'
                        }`}>
                          <User className={`w-5 h-5 ${
                            u.role === 'super_admin' ? 'text-purple-600' :
                            u.role === 'admin' ? 'text-violet-700' :
                            u.role === 'coordinatore' ? 'text-blue-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{u.nome}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getRoleBadge(u.role)}
                        {u.is_active === false && (
                          <Badge variant="outline" className="text-red-500 border-red-200">
                            Disattivato
                          </Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {hasPermission("users:update") && (<>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateUserRole(u.id, 'operatore')}
                              disabled={u.role === 'operatore'}
                            >
                              <User className="w-4 h-4 mr-2" /> Imposta Operatore
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateUserRole(u.id, 'coordinatore')}
                              disabled={u.role === 'coordinatore'}
                            >
                              <UserCog className="w-4 h-4 mr-2" /> Imposta Coordinatore
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateUserRole(u.id, 'amministrativo')}
                              disabled={u.role === 'amministrativo'}
                            >
                              <Building2 className="w-4 h-4 mr-2" /> Imposta Amministrativo
                            </DropdownMenuItem>
                            {isSuperAdminRole && (
                              <DropdownMenuItem 
                                onClick={() => handleUpdateUserRole(u.id, 'super_admin')}
                                disabled={u.role === 'super_admin'}
                              >
                                <Crown className="w-4 h-4 mr-2" /> Imposta Super Admin
                              </DropdownMenuItem>
                            )}
                            {isSuperAdminRole && permissionsCatalog && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleOpenEditPermissions(u)}>
                                  <Lock className="w-4 h-4 mr-2 text-indigo-500" /> Modifica permessi
                                  {u.permissions && u.permissions.length > 0 && (
                                    <Badge variant="outline" className="ml-auto text-[10px] px-1 py-0 border-indigo-300 text-indigo-600">custom</Badge>
                                  )}
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedUser(u);
                                setShowResetPasswordModal(true);
                              }}
                            >
                              <Key className="w-4 h-4 mr-2" /> Reset Password
                            </DropdownMenuItem>
                            </>)}
                            {hasPermission("users:delete") && (<>
                            <DropdownMenuSeparator />
                            {u.is_active !== false ? (
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleToggleUserStatus(u.id, true)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Disattiva
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                className="text-green-600"
                                onClick={() => handleToggleUserStatus(u.id, false)}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" /> Riattiva
                              </DropdownMenuItem>
                            )}
                            </>)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </>)}
        {/* Nota: TAB UTENTI continua più sotto con Notifiche e Sicurezza */}

        {/* ========== TAB: STRUTTURA (parte 2/3 - Retention + Contratto) ========== */}
        {safeActiveTab === "struttura" && (<>
        {/* SEZIONE: DATA RETENTION (solo super_admin e amministrativo) */}
        {canViewRetention && (
          <Card className="card-base">
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                <Database className="w-4 h-4 md:w-5 md:h-5 text-purple-500" />
                Gestione Dati e Retention
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                Configura la policy di conservazione dati GDPR
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0 md:pt-0 space-y-4">
              {loadingRetention ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : retentionConfig ? (
                <>
                  {/* Config attuale */}
                  <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2 text-slate-700">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Periodo di Retention</span>
                    </div>
                    
                    <div className="grid gap-4">
                      <div>
                        <Label className="text-sm text-slate-600">Giorni di conservazione dopo dimissione</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            min="365"
                            max="3650"
                            value={retentionConfig.retention_days || 730}
                            onChange={(e) => setRetentionConfig({
                              ...retentionConfig,
                              retention_days: parseInt(e.target.value) || 730
                            })}
                            className="w-32"
                            disabled={!isSuperAdminRole}
                          />
                          <span className="text-sm text-slate-500">
                            ({Math.round((retentionConfig.retention_days || 730) / 365 * 10) / 10} anni)
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Min: 365 giorni (1 anno) - Max: 3650 giorni (10 anni)</p>
                      </div>

                      <div>
                        <Label className="text-sm text-slate-600">Giorni preavviso scadenza</Label>
                        <Input
                          type="number"
                          min="7"
                          max="90"
                          value={retentionConfig.notification_days_before || 30}
                          onChange={(e) => setRetentionConfig({
                            ...retentionConfig,
                            notification_days_before: parseInt(e.target.value) || 30
                          })}
                          className="w-32 mt-1"
                          disabled={!isSuperAdminRole}
                        />
                      </div>

                      <div className="flex items-center justify-between py-2 border-t">
                        <div>
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            Cancellazione automatica
                          </Label>
                          <p className="text-xs text-slate-500">
                            Cancella automaticamente i fascicoli scaduti
                          </p>
                        </div>
                        <Switch
                          checked={retentionConfig.auto_delete_enabled || false}
                          onCheckedChange={(checked) => setRetentionConfig({
                            ...retentionConfig,
                            auto_delete_enabled: checked
                          })}
                          disabled={!isSuperAdminRole}
                        />
                      </div>
                    </div>

                    {isSuperAdminRole && (
                      <Button 
                        onClick={saveRetentionConfig} 
                        disabled={savingRetention}
                        className="w-full mt-4"
                      >
                        {savingRetention ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-2" /> Salva Configurazione</>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Report Retention */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-700 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Report Retention
                        </h4>
                        <p className="text-xs text-slate-500">Visualizza lo stato dei fascicoli</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchRetentionReport}>
                        <Download className="w-4 h-4 mr-2" /> Genera Report
                      </Button>
                    </div>
                  </div>

                  {/* Policy Description */}
                  <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                    <p className="font-medium mb-1">Policy GDPR (Art. 17)</p>
                    <p className="text-xs">
                      I dati degli ospiti vengono conservati per {retentionConfig.retention_days || 730} giorni 
                      ({Math.round((retentionConfig.retention_days || 730) / 365 * 10) / 10} anni) dalla data di dimissione. 
                      Trascorso questo periodo, i dati {retentionConfig.auto_delete_enabled ? 
                        "vengono cancellati automaticamente" : 
                        "possono essere cancellati manualmente"}.
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Configurazione non disponibile</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modal Report Retention */}
        <Dialog open={showRetentionReport} onOpenChange={setShowRetentionReport}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Report Retention Fascicoli
              </DialogTitle>
              <DialogDescription>
                Stato della conservazione dati per la tua struttura
              </DialogDescription>
            </DialogHeader>
            
            {retentionReport && (
              <div className="space-y-4">
                {/* Statistiche */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-700">{retentionReport.statistics?.total || 0}</p>
                    <p className="text-xs text-slate-500">Totale</p>
                  </div>
                  <div className="bg-green-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{retentionReport.statistics?.active || 0}</p>
                    <p className="text-xs text-green-600">Attivi</p>
                  </div>
                  <div className="bg-amber-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{retentionReport.statistics?.expiring_soon || 0}</p>
                    <p className="text-xs text-amber-600">In Scadenza</p>
                  </div>
                  <div className="bg-red-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{retentionReport.statistics?.expired || 0}</p>
                    <p className="text-xs text-red-600">Scaduti</p>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 rounded-lg p-3 text-sm">
                  <p><strong>Retention:</strong> {retentionReport.retention_days} giorni</p>
                  <p><strong>Preavviso:</strong> {retentionReport.notification_days} giorni</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Report generato: {new Date(retentionReport.generated_at).toLocaleString('it-IT')}
                  </p>
                </div>

                {/* Lista fascicoli */}
                {retentionReport.items?.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
                      Dettaglio Fascicoli ({retentionReport.items.length})
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {retentionReport.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between px-3 py-2 border-b last:border-0 text-sm">
                          <div>
                            <p className="font-medium">{item.nome_ospite || 'N/D'}</p>
                            <p className="text-xs text-slate-500">
                              {item.data_dimissione ? 
                                `Dimesso: ${new Date(item.data_dimissione).toLocaleDateString('it-IT')}` : 
                                'Non dimesso'}
                            </p>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(item.status)}
                            {item.giorni_rimanenti !== null && (
                              <p className="text-xs text-slate-500 mt-1">
                                {item.giorni_rimanenti > 0 ? `${item.giorni_rimanenti} giorni` : 'Scaduto'}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRetentionReport(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SEZIONE: TEMPLATE CONTRATTO */}
        <Card className="card-base">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <FileSignature className="w-4 h-4 md:w-5 md:h-5 text-indigo-500" />
              Template Contratto
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Configura logo e testo del contratto per la firma digitale degli ospiti
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0 space-y-5">
            {loadingContract ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Logo struttura */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Logo struttura</Label>
                  <p className="text-xs text-slate-500">PNG, JPG o WEBP — max 2 MB. Appare in intestazione al contratto.</p>
                  {(contractLogoPreview || contractLogoSavedUrl) ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={contractLogoPreview || contractLogoSavedUrl}
                        alt="Logo struttura"
                        className="h-14 max-w-[160px] object-contain border border-slate-200 rounded p-1 bg-white"
                      />
                      <div className="flex flex-col gap-1">
                        <label htmlFor="contract-logo-upload" className="cursor-pointer">
                          <Button variant="outline" size="sm" asChild>
                            <span><Upload className="w-3 h-3 mr-1" />Sostituisci</span>
                          </Button>
                        </label>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={handleRemoveContractLogo}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />Rimuovi
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <label htmlFor="contract-logo-upload" className="cursor-pointer">
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-5 text-center hover:border-indigo-400 transition-colors">
                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">Carica logo (opzionale)</p>
                      </div>
                    </label>
                  )}
                  <input
                    type="file"
                    id="contract-logo-upload"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleContractLogoChange}
                    className="hidden"
                  />
                </div>

                {/* Testo contratto */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label className="text-sm font-medium">Testo del contratto <span className="text-red-500">*</span></Label>
                    <div className="flex items-center gap-2">
                      {contractBodyText.trim() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-indigo-600 hover:bg-indigo-50 text-xs"
                          onClick={() => setShowContractTextPreview(true)}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Anteprima testo
                        </Button>
                      )}
                      <label htmlFor="contract-pdf-import" className="cursor-pointer">
                        <Button variant="outline" size="sm" asChild disabled={extractingPdfText}>
                          <span>
                            {extractingPdfText ? (
                              <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Estrazione...</>
                            ) : (
                              <><Upload className="w-3 h-3 mr-1" />Importa da PDF</>
                            )}
                          </span>
                        </Button>
                      </label>
                      <input
                        type="file"
                        id="contract-pdf-import"
                        accept="application/pdf"
                        onChange={handleExtractPdfText}
                        className="hidden"
                        disabled={extractingPdfText}
                      />
                    </div>
                  </div>
                  <textarea
                    value={contractBodyText}
                    onChange={(e) => setContractBodyText(e.target.value)}
                    rows={16}
                    placeholder="Inserisci qui il testo completo del contratto di ospitalità della struttura..."
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y font-mono"
                  />
                  <p className="text-xs text-slate-400">{contractBodyText.length} caratteri</p>
                </div>

                {/* Azioni */}
                <div className="flex items-center justify-between gap-3 pt-1">
                  {contractTemplate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={handleDeleteContract}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Elimina configurazione
                    </Button>
                  )}
                  <div className="ml-auto">
                    <Button
                      onClick={handleSaveContractTemplate}
                      disabled={savingContract || !contractBodyText.trim()}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {savingContract ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio...</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" />Salva configurazione</>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                  <p className="font-medium mb-1 flex items-center gap-2">
                    <PenLine className="w-4 h-4" />
                    Come funziona
                  </p>
                  <p className="text-xs">
                    Il PDF del contratto viene generato automaticamente con: intestazione (logo + dati ospite/familiare),
                    testo del contratto, e una pagina dedicata alla firma con il canvas disegno.
                    La sigla della firma viene applicata nel footer di ogni pagina.
                    Il documento firmato viene salvato nei documenti del fascicolo.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Dialog anteprima testo contratto */}
        <Dialog open={showContractTextPreview} onOpenChange={setShowContractTextPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-500" />
                Anteprima testo contratto
              </DialogTitle>
            </DialogHeader>
            <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed border rounded-lg p-4 bg-slate-50">
              {contractBodyText}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContractTextPreview(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </>)}
        {/* ========== FINE TAB: STRUTTURA ========== */}

        {/* ========== TAB: UTENTI (parte 2/3 - Notifiche + Sicurezza) ========== */}
        {safeActiveTab === "utenti" && (<>
        {/* SEZIONE: NOTIFICHE */}
        <Card className="card-base">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Bell className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
              Notifiche Email
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Scegli quali notifiche ricevere via email
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0 space-y-3 md:space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  Richieste Personale
                </Label>
                <p className="text-xs md:text-sm text-slate-500 ml-6">Aggiornamenti sulle tue richieste</p>
              </div>
              <Switch 
                checked={notifications.email_richieste}
                onCheckedChange={(checked) => setNotifications({...notifications, email_richieste: checked})}
                className="shrink-0"
              />
            </div>
            
            <div className="flex items-center justify-between py-2 border-t">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  Nuovi CV
                </Label>
                <p className="text-xs md:text-sm text-slate-500 ml-6">Quando arrivano nuovi candidati</p>
              </div>
              <Switch 
                checked={notifications.email_cv}
                onCheckedChange={(checked) => setNotifications({...notifications, email_cv: checked})}
                className="shrink-0"
              />
            </div>
            
            <div className="flex items-center justify-between py-2 border-t">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <Zap className="w-4 h-4 text-slate-400" />
                  Promozioni Gruppi d&apos;Acquisto
                </Label>
                <p className="text-xs md:text-sm text-slate-500 ml-6">Nuove promozioni disponibili</p>
              </div>
              <Switch 
                checked={notifications.email_promo}
                onCheckedChange={(checked) => setNotifications({...notifications, email_promo: checked})}
                className="shrink-0"
              />
            </div>
            
            <div className="flex items-center justify-between py-2 border-t">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  PAI in scadenza
                </Label>
                <p className="text-xs md:text-sm text-slate-500 ml-6">Promemoria scadenze PAI</p>
              </div>
              <Switch 
                checked={notifications.email_pai}
                onCheckedChange={(checked) => setNotifications({...notifications, email_pai: checked})}
                className="shrink-0"
              />
            </div>
          </CardContent>
        </Card>

        <PushNotificationsSettings token={token} />

        {/* SEZIONE: SICUREZZA */}
        <Card className="card-base">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Shield className="w-4 h-4 md:w-5 md:h-5 text-slate-500" />
              Sicurezza
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Gestisci la sicurezza del tuo account
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0 space-y-3 md:space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <UserPen className="w-4 h-4 text-slate-400" />
                  Nome Utente
                </Label>
                <p className="text-xs md:text-sm text-slate-500 break-all">{user?.name || 'Non disponibile'}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs md:text-sm shrink-0"
                onClick={() => {
                  setNewName(user?.name || "");
                  setShowNameModal(true);
                }}
              >
                Modifica
              </Button>
            </div>
            
            <div className="flex items-center justify-between py-2 border-t">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <Key className="w-4 h-4 text-slate-400" />
                  Cambia Password
                </Label>
                <p className="text-xs md:text-sm text-slate-500">Aggiorna la password del tuo account</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs md:text-sm shrink-0"
                onClick={() => setShowPasswordModal(true)}
              >
                Modifica
              </Button>
            </div>
            
            <div className="flex items-center justify-between py-2 border-t">
              <div className="flex-1 mr-3">
                <Label className="text-sm md:text-base font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  Email account
                </Label>
                <p className="text-xs md:text-sm text-slate-500 break-all">{user?.email || 'Non disponibile'}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs md:text-sm shrink-0"
                onClick={() => {
                  setNewEmail(user?.email || "");
                  setShowEmailModal(true);
                }}
              >
                Modifica
              </Button>
            </div>
          </CardContent>
        </Card>
        </>)}
        {/* ========== FINE TAB: UTENTI ========== */}

        {/* ========== TAB: DATI FISCALI ========== */}
        {safeActiveTab === "fiscali" && (<>
        {/* SEZIONE: IMPOSTAZIONI RETTA MENSILE */}
        {canAccessFatturazioneElettronica(user) ? (
          <ImpostazioniRettaMensile token={token} orgStrutture={orgStrutture} />
        ) : null}
        {/* SEZIONE: FATTURAZIONE ELETTRONICA */}
        {canAccessFatturazioneElettronica(user) ? (
          <FatturazioneElettronicaImpostazioni token={token} orgStrutture={orgStrutture} />
        ) : (
          <Card className="card-base">
            <CardContent className="p-6 md:p-8 text-center">
              <Receipt className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm text-slate-500">
                La fatturazione elettronica non è disponibile per il tuo piano.
              </p>
            </CardContent>
          </Card>
        )}
        </>)}
        {/* ========== FINE TAB: DATI FISCALI ========== */}

        {/* ========== TAB: PAGAMENTI (placeholder) ========== */}
        {safeActiveTab === "pagamenti" && (<>
        <Card className="card-base">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
              Pagamenti
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Metodi di pagamento e cronologia delle fatture
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0 space-y-4">
            {recruitingPlan?.active ? (
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Pagamento ricorrente attivo</p>
                      <p className="text-xs text-slate-500">
                        Piano Recruiting {recruitingPlan.plan?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500 text-white text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Attivo
                  </Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Importo mensile</span>
                    <span className="font-semibold text-slate-900">
                      {recruitingPlan.plan === 'pro' ? '89€' : '49€'}/mese
                    </span>
                  </div>
                  {recruitingPlan.activated_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Attivo dal</span>
                      <span className="text-slate-900">
                        {new Date(recruitingPlan.activated_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab("abbonamento")}
                  >
                    Gestisci abbonamento
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:border-red-300"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Disdici
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Nessun pagamento ricorrente attivo</p>
                <p className="text-xs mt-1">
                  Attiva un piano nella sezione <button type="button" className="text-blue-600 hover:underline" onClick={() => setActiveTab("abbonamento")}>Abbonamento</button> per iniziare
                </p>
              </div>
            )}

            <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Cronologia pagamenti
              </p>
              <p className="text-xs">
                La cronologia completa delle fatture Affidya sarà disponibile prossimamente.
                Per richiedere una copia delle ricevute, contatta il supporto.
              </p>
            </div>
          </CardContent>
        </Card>
        </>)}
        {/* ========== FINE TAB: PAGAMENTI ========== */}

        {/* ========== TAB: SUPPORTO ========== */}
        {safeActiveTab === "whatsapp-ops" && canManageOrgUsers && user?.whatsapp_ops_enabled === true && (
          <WhatsAppOpsBindingsPanel
            mode="user"
            authHeaders={{ Authorization: `Bearer ${token}` }}
          />
        )}

        {safeActiveTab === "supporto" && (<>
        {/* SEZIONE: ASSISTENZA */}
        <Card className="card-base bg-slate-50">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm md:text-base font-medium text-slate-900">Hai bisogno di aiuto?</p>
                <p className="text-xs md:text-sm text-slate-500">Contatta il supporto Affidya</p>
              </div>
              <Button variant="outline" size="sm" asChild className="text-xs md:text-sm shrink-0">
                <a href="mailto:supporto@affidya.it">
                  Contattaci
                  <ExternalLink className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="p-4 md:p-6">
            <h4 className="font-medium text-slate-900 text-sm md:text-base mb-2">Documentazione</h4>
            <p className="text-xs md:text-sm text-slate-500 mb-3">
              Consulta guide, FAQ e risorse utili per sfruttare al massimo la piattaforma.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" asChild className="text-xs md:text-sm">
                <a href="https://affidya.it/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild className="text-xs md:text-sm">
                <a href="https://affidya.it/terms" target="_blank" rel="noopener noreferrer">
                  Termini di Servizio
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
        </>)}
        {/* ========== FINE TAB: SUPPORTO ========== */}
        </div>
      </div>

      {/* Modal Conferma Disdetta */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Conferma Disdetta
            </DialogTitle>
            <DialogDescription>
              Sei sicuro di voler disdire il piano {recruitingPlan?.plan?.toUpperCase()}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Cosa succede dopo la disdetta:</p>
                  <ul className="mt-2 space-y-1 text-amber-700">
                    <li>• Il piano rimarrà attivo fino alla fine del periodo pagato</li>
                    <li>• Non verranno addebitati ulteriori importi</li>
                    <li>• Perderai accesso a &quot;Le Mie Richieste&quot;</li>
                    <li>• Potrai riattivare in qualsiasi momento</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)} disabled={cancelling}>
              Annulla
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelling}
            >
              {cancelling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Conferma disdetta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifica Email */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Modifica Email
            </DialogTitle>
            <DialogDescription>
              Inserisci la nuova email per il tuo account. Dopo la modifica dovrai effettuare nuovamente il login.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="new-email" className="text-sm font-medium">Nuova Email</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="nuova@email.it"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)} disabled={savingCredentials}>
              Annulla
            </Button>
            <Button 
              onClick={handleChangeEmail}
              disabled={savingCredentials || !newEmail}
            >
              {savingCredentials ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifica Password */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Modifica Password
            </DialogTitle>
            <DialogDescription>
              Inserisci la password attuale e quella nuova. La nuova password deve avere almeno 10 caratteri,
              una maiuscola, una minuscola, un numero e un carattere speciale tra{" "}
              <span className="font-mono text-xs">{"@$!%*?&._-#"}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="current-password" className="text-sm font-medium">Password Attuale</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new-password" className="text-sm font-medium">Nuova Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-sm font-medium">Conferma Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)} disabled={savingCredentials}>
              Annulla
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={savingCredentials || !currentPassword || !newPassword}
            >
              {savingCredentials ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifica Nome Utente */}
      <Dialog open={showNameModal} onOpenChange={setShowNameModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPen className="w-5 h-5 text-emerald-500" />
              Modifica Nome Utente
            </DialogTitle>
            <DialogDescription>
              Inserisci il nuovo nome che verrà visualizzato nel tuo account.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="new-name" className="text-sm font-medium">Nome Utente</Label>
              <Input
                id="new-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Mario Rossi"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNameModal(false)} disabled={savingCredentials}>
              Annulla
            </Button>
            <Button 
              onClick={handleChangeName}
              disabled={savingCredentials || !newName || newName.trim().length < 2}
            >
              {savingCredentials ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crea Nuovo Utente */}
      <Dialog
        open={showCreateUserModal}
        onOpenChange={(open) => {
          setShowCreateUserModal(open);
          if (!open) setCustomPermissions(null);
        }}
      >
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:rounded-lg">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-slate-200 px-6 pb-4 pt-6 text-left">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <UserPlus className="w-5 h-5 text-purple-500" />
              Crea Nuovo Utente
            </DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo operatore, coordinatore o admin alla piattaforma.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-4">
            <div>
              <Label htmlFor="user-nome" className="text-sm font-medium">Nome Completo *</Label>
              <Input
                id="user-nome"
                type="text"
                value={newUser.nome}
                onChange={(e) => setNewUser({...newUser, nome: e.target.value})}
                placeholder="Mario Rossi"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="user-email" className="text-sm font-medium">Email *</Label>
              <Input
                id="user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="mario.rossi@struttura.it"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="user-password" className="text-sm font-medium">Password Temporanea *</Label>
              <Input
                id="user-password"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="Min. 6 caratteri"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">L'utente potrà cambiarla al primo accesso</p>
            </div>
            <div>
              <Label htmlFor="user-role" className="text-sm font-medium">Ruolo *</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(value) => { setNewUser({...newUser, role: value}); setCustomPermissions(null); }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatore">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> Operatore
                    </div>
                  </SelectItem>
                  <SelectItem value="coordinatore">
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4" /> Coordinatore
                    </div>
                  </SelectItem>
                  <SelectItem value="amministrativo">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Amministrativo
                    </div>
                  </SelectItem>
                  {isSuperAdminRole && (
                    <SelectItem value="super_admin">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4" /> Super Admin
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {(() => {
                const roleDescriptions = {
                  operatore: 'Visualizza fascicoli, crea note nel diario',
                  coordinatore: 'Modifica fascicoli, crea PAI e valutazioni',
                  amministrativo: 'Gestione utenti, billing, audit log',
                  super_admin: 'Accesso completo, gestione utenti, export dati',
                };
                return (
                  <div className="mt-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
                    {roleDescriptions[newUser.role] || ''}
                  </div>
                );
              })()}
            </div>

            {/* Permessi personalizzati (solo super_admin + catalogo disponibile) */}
            {isSuperAdminRole && permissionsCatalog && (
              <div>
                <Accordion type="single" collapsible>
                  <AccordionItem value="permissions" className="border rounded-lg">
                    <AccordionTrigger className="px-3 py-2 hover:no-underline text-sm font-medium text-slate-700">
                      <div className="flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5 text-indigo-400" />
                        Permessi personalizzati
                        <span className="text-xs font-normal text-slate-400">(opzionale — default dal ruolo)</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3 data-[state=open]:overflow-visible">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-slate-500">Deseleziona per limitare l'accesso rispetto al ruolo scelto.</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-7 text-slate-500 shrink-0"
                          onClick={() => setCustomPermissions(null)}
                        >
                          Ripristina default
                        </Button>
                      </div>
                      <div className="max-h-[min(50vh,22rem)] overflow-y-auto overscroll-contain rounded-md border border-slate-100 bg-white/80 p-2 sm:max-h-[min(55vh,28rem)]">
                        <PermissionsChecklist
                          catalog={permissionsCatalog}
                          selectedPermissions={customPermissions ?? getDefaultPermissionsForRole(newUser.role)}
                          onToggle={(permId, checked) => {
                            setCustomPermissions((prev) => {
                              const base =
                                prev !== null
                                  ? prev
                                  : [...getDefaultPermissionsForRole(newUser.role)];
                              if (checked) {
                                return [...new Set([...base, permId])];
                              }
                              return base.filter((p) => p !== permId);
                            });
                          }}
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}
          </div>
          <DialogFooter className="shrink-0 gap-2 border-t border-slate-200 bg-slate-50/80 px-6 py-4 sm:rounded-b-lg">
            <Button variant="outline" onClick={() => { setShowCreateUserModal(false); setCustomPermissions(null); }} disabled={savingUser}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={savingUser || !newUser.email || !newUser.nome || !newUser.password}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingUser ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Crea Utente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reset Password Utente */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Imposta una nuova password per {selectedUser?.nome} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="reset-password" className="text-sm font-medium">Nuova Password</Label>
              <Input
                id="reset-password"
                type="text"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Min. 6 caratteri"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">L'utente dovrà usare questa password per accedere</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetPasswordModal(false);
              setResetPassword("");
              setSelectedUser(null);
            }} disabled={savingUser}>
              Annulla
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={savingUser || !resetPassword || resetPassword.length < 6}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingUser ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifica Permessi Utente (solo super_admin) */}
      <Dialog open={showEditPermissionsModal} onOpenChange={(open) => {
        setShowEditPermissionsModal(open);
        if (!open) { setSelectedUser(null); setEditCustomPermissions([]); }
      }}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:rounded-lg">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-slate-200 px-6 pb-4 pt-6 text-left">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <Lock className="w-5 h-5 text-indigo-500" />
              Modifica Permessi — {selectedUser?.nome}
            </DialogTitle>
            <DialogDescription>
              Personalizza i permessi di accesso per questo utente. Il ruolo attuale è{" "}
              <strong>{selectedUser?.role}</strong>. Le modifiche sovrascrivono il default del ruolo.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
            {selectedUser && permissionsCatalog && (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    {editCustomPermissions.length} permessi selezionati su {permissionsCatalog.permissions.length}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => setEditCustomPermissions(getDefaultPermissionsForRole(selectedUser.role))}
                    >
                      Default {selectedUser.role}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => setEditCustomPermissions([])}
                    >
                      Deseleziona tutto
                    </Button>
                  </div>
                </div>
                <div className="max-h-[min(55vh,32rem)] overflow-y-auto overscroll-contain rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <PermissionsChecklist
                    catalog={permissionsCatalog}
                    selectedPermissions={editCustomPermissions}
                    onToggle={(permId, checked) => {
                      setEditCustomPermissions((prev) => {
                        const base = prev || [];
                        if (checked) {
                          return [...new Set([...base, permId])];
                        }
                        return base.filter((p) => p !== permId);
                      });
                    }}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="shrink-0 gap-2 border-t border-slate-200 bg-slate-50/80 px-6 py-4 sm:rounded-b-lg">
            <Button variant="outline" onClick={() => {
              setShowEditPermissionsModal(false);
              setSelectedUser(null);
              setEditCustomPermissions([]);
            }} disabled={savingUser}>
              Annulla
            </Button>
            <Button
              onClick={handleSaveUserPermissions}
              disabled={savingUser}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingUser ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4 mr-2" />
              )}
              Salva permessi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
